import { styled } from "styled-components";

import { bookmarksApiSlice } from "api/bookmarksApi.slice";
import type { EmergingTheme } from "api/schemas/emergingThemesApiSchemas";
import { UIActionButton, UIButtonsRow } from "common/components";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTrack } from "common/utilities/useTrack";
import type { TrackPage } from "domains/analytics/types";
import type { Cluster } from "domains/clusters/types";
import { shareLinkAction } from "domains/share/actions";
import { TopicIcons } from "domains/topic/components/TopicIcons";
import { getCanDisplayBookmarks } from "entities/permissions/selectors";

const StyledFooter = styled.div`
  display: flex;
  margin-top: 1rem;
  justify-content: space-between;
  align-items: center;
  flex-flow: wrap;
`;

type ClusterFooterProps = {
  cluster: Cluster | EmergingTheme;
  shareLink?: string;
  showIcons: boolean;
  trackPage?: TrackPage;
};

export const ClusterFooter = ({ cluster, showIcons, shareLink, trackPage }: ClusterFooterProps) => {
  const track = useTrack();
  const dispatch = useDispatch();

  const { id, title, related_topics, is_bookmarked } = cluster;

  const accessToBookmarks = useSelector(getCanDisplayBookmarks);

  const [addKnowledgeToBookmarks] = bookmarksApiSlice.endpoints.addKnowledgeToBookmarks.useMutation();
  const [removeKnowledgeFromBookmarks] = bookmarksApiSlice.endpoints.removeKnowledgeFromBookmarks.useMutation();

  const onBookmarkHandle = async () => {
    if ("is_bookmarked" in cluster && cluster.is_bookmarked) {
      await removeKnowledgeFromBookmarks({ id: cluster.id });
    } else {
      await addKnowledgeToBookmarks({ id: cluster.id });
    }

    track({
      entityId: id,
      entityName: cluster.title,
      entityType: "Cluster",
      page: trackPage,
      name: "Bookmark",
    });
  };

  const onShareClick = () =>
    dispatch(
      shareLinkAction({
        id: id,
        name: title,
        link: `${import.meta.env.VITE_WEBSITE_URL}${shareLink}`,
        type: "Cluster",
      }),
    );

  return (
    <StyledFooter>
      <UIButtonsRow>
        {shareLink && <UIActionButton type="share" onClick={onShareClick} />}
        {accessToBookmarks && <UIActionButton type="bookmark" isActive={is_bookmarked} onClick={onBookmarkHandle} />}
      </UIButtonsRow>
      {showIcons && <TopicIcons topics={related_topics} />}
    </StyledFooter>
  );
};
